// useShortMessageCta.js
import { useEffect, useRef, useState, useMemo } from 'react';

import getIsMatchedPathname from '../resource/getIsMatchedPathname.js';

const TRANSITION_MILLI_SECONDS = 400; // TODO: remote config

export const CtaType = {
  UNLOCK: 'unlock',
  LIVESTREAM: 'livestream',
  SHORTS: 'shorts',
  PUBLISH: 'publish',
};
export const CtaStatus = {
  DISPLAY: 'display',
  HIDDEN: 'hidden',
  OPENING: 'opening',
};

/**
 * Short message cta
 * @typedef {object} ShortMessageCta
 * @property {CtaStatus} ctaStatus - cta status. value is one of ['hidden', 'display', 'opening'].
 * @property {CtaType} ctaType - cta type.
 */

/**
 * Use short message cta
 * @kind hook
 * @param {string} {ctaUrl} - cta url link.
 * @param {string} {meId} - me id.
 * @param {boolean} {isStreaming} - is streaming.
 * @param {boolean} {isActive} - is short message actived.
 * @param {boolean} {canViewMedia} - can view message.
 * @param {boolean} {isPublished} - is published message.
 * @param {number} {[unlockCtaDelaySeconds = 0]} - unlock cta delay seconds.
 * @param {number} {livestreamCtaDelaySeconds} - livestream cta delay seconds.
 * @param {number} {ctaDelaySeconds} - cta delay seconds.
 * @param {number} {unlockPrice} - message unlock message.
 * @return {ShortMessageCta} Return short message cta object.
 */
const useShortMessageCta = ({
  ctaUrl,
  meId,
  isStreaming,
  isActive,
  canViewMedia,
  isPublished,
  unlockCtaDelaySeconds = 0,
  livestreamCtaDelaySeconds,
  ctaDelaySeconds,
  unlockPrice,
}) => {
  const ctaTimeout = useRef(null);
  const ctaType = useMemo(() => {
    if (!isPublished) {
      return CtaType.PUBLISH;
    }
    if (!ctaUrl && unlockPrice > 0) {
      return CtaType.UNLOCK;
    }
    if (ctaUrl) {
      const url = new URL(ctaUrl, 'https://swag.live');
      const isLivestreamDetail = getIsMatchedPathname({
        pathname: url.pathname,
        dataKey: 'isOnLivestreamDetail',
      });
      if (isLivestreamDetail) {
        return CtaType.LIVESTREAM;
      }
    }
    return CtaType.SHORTS;
  }, [ctaUrl, isPublished, unlockPrice]);
  const delaySeconds = useMemo(() => {
    switch (ctaType) {
      case CtaType.PUBLISH: {
        return 0;
      }
      case CtaType.UNLOCK: {
        return unlockCtaDelaySeconds;
      }
      case CtaType.LIVESTREAM: {
        return livestreamCtaDelaySeconds;
      }
      case CtaType.SHORTS:
      default: {
        return ctaDelaySeconds;
      }
    }
  }, [
    ctaDelaySeconds,
    ctaType,
    livestreamCtaDelaySeconds,
    unlockCtaDelaySeconds,
  ]);
  const initCtaStatus = useMemo(() => {
    if (ctaDelaySeconds < 1) {
      if (CtaType.PUBLISH === ctaType) {
        return CtaStatus.DISPLAY;
      }
      if (CtaType.UNLOCK === ctaType) {
        return !canViewMedia ? CtaStatus.DISPLAY : CtaStatus.HIDDEN;
      }
      if (CtaType.LIVESTREAM === ctaType) {
        return isStreaming || !meId ? CtaStatus.DISPLAY : CtaStatus.HIDDEN;
      }
      if (ctaUrl) {
        return CtaStatus.DISPLAY;
      }
    }
    return CtaStatus.HIDDEN;
  }, [canViewMedia, ctaDelaySeconds, ctaType, ctaUrl, isStreaming, meId]);
  const [ctaStatus, setCtaStatus] = useState(initCtaStatus);
  useEffect(() => {
    if (isActive && ctaDelaySeconds >= 1) {
      switch (true) {
        case CtaType.UNLOCK === ctaType:
        case CtaType.LIVESTREAM === ctaType && ctaUrl && (isStreaming || !meId):
        case CtaType.SHORTS === ctaType && ctaUrl: {
          clearTimeout(ctaTimeout.current);
          ctaTimeout.current = setTimeout(() => {
            setCtaStatus('opening');
            ctaTimeout.current = setTimeout(() => {
              setCtaStatus('display');
            }, TRANSITION_MILLI_SECONDS);
          }, delaySeconds * 1000);
        }
      }
    } else {
      setCtaStatus(initCtaStatus);
    }
    return () => {
      clearTimeout(ctaTimeout.current);
    };
  }, [
    ctaDelaySeconds,
    ctaType,
    ctaUrl,
    delaySeconds,
    initCtaStatus,
    isActive,
    isStreaming,
    meId,
  ]);
  return {
    ctaStatus,
    ctaType,
  };
};

export default useShortMessageCta;
